import { useEffect, useState } from "react";

import { Spinner } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { Navigate, useNavigate, useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { extractUrlSearchParams } from "@/components/helpers/url-utils";
import { isCoach } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsToXlQuery } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { TimeslotCard } from "@/components/pages_logged_in/schedule/timeslots/TimeslotCard";
import { HighlightText } from "@/components/pieces/HighlightText";
import { BackButton } from "@/components/pieces/onboarding/BackButton";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import { SIDE_NAV_WIDTH, SITE_NAME } from "@/css/constants";
import {
  RescheduleStatus,
  useMeetingDetailQuery,
  useRescheduleCoachMeetingMutation,
} from "@/graphql";
import { formatDate } from "@/utils/datetime";

import { ConfirmRescheduleDialog } from "./ConfirmRescheduleDialog";
import { ErrorModal } from "./ErrorModal";
import { isMeetingRefundable } from "./is-meeting-refundable";

export function AuthReschedule({ customerId }: { customerId?: string }) {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [visible, setVisible] = useState(false);
  const [timeslots, setTimeslots] = useState<string[]>([]);
  const [error, setError] = useState<RescheduleStatus | null>(null);
  const [isRefundException, setIsRefundException] = useState(false);
  const [showAllTimes, setShowAllTimes] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const isXsToLgScreen = useMediaQuery(isXsToXlQuery);

  const { data: meetingData, loading: loadingMeeting } = useMeetingDetailQuery({
    variables: {
      id: meetingId ?? "",
      customerId: customerId,
    },
    skip: !meetingId,
  });

  useEffect(() => {
    if (meetingData) {
      const { meeting } = meetingData;
      const { canceledAt } = meeting || {};
      if (canceledAt) navigate(-1);
      setIsRefundException(isCoach() && !isMeetingRefundable(meeting));
    }
  }, [meetingData, navigate]);

  const [rescheduleCoachMeeting, { loading: rescheduling }] =
    useRescheduleCoachMeetingMutation({
      refetchQueries: ["Sessions"],
    });

  const onError = (status?: RescheduleStatus | null) => {
    setVisible(true);
    setError(status ?? RescheduleStatus.Error);
  };

  const handleReschedule = async () => {
    if (!meetingId || timeslots.length === 0) {
      onError();
      return;
    }

    setVisible(false);

    try {
      const { data } = await rescheduleCoachMeeting({
        variables: {
          meetingId,
          startTime: timeslots[0],
          message: rescheduleReason,
          forceRefund: isRefundException,
          customerId: customerId,
          usedAllTimeslot: showAllTimes,
        },
      });

      const status = data?.rescheduleCoachMeeting?.status;

      if (status === RescheduleStatus.Success) {
        const callbackUrl = extractUrlSearchParams(
          window.location.href,
          "callbackUrl",
        );

        if (callbackUrl) {
          setNavigateTo(callbackUrl);
          return;
        }

        const newMeetingId = data?.rescheduleCoachMeeting?.meeting?.id;
        if (!newMeetingId) {
          setNavigateTo("/sessions");
          return;
        }

        setNavigateTo(`/sessions/${newMeetingId}`);
      } else {
        onError(status);
      }
    } catch (_) {
      onError();
    }
  };

  if (navigateTo) {
    return <Navigate to={navigateTo} replace />;
  }

  const { meeting } = meetingData || {};

  const { customer, product, coach, workspace, programComponent } =
    meeting || {};

  const displayName =
    currentUser.id == coach?.id ? customer?.name : coach?.name;

  const { name: displayWorkspace = "Personal" } = workspace || {};
  const isLoading = loadingMeeting || rescheduling;

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems={isXsToLgScreen ? "flex-start" : "center"}
        css={{ position: "relative" }}
      >
        <BackButton mediaQuery={isXsToXlQuery} onClick={() => navigate(-1)} />
        <Box width={[1, 1, 1, 1024]} maxWidth="100%">
          <PageTitle
            title={
              <>
                Reschedule your <HighlightText>{product?.name}</HighlightText>{" "}
                with <HighlightText>{displayName}</HighlightText> in{" "}
                <HighlightText>{displayWorkspace}</HighlightText>
              </>
            }
            style={{
              textAlign: "center",
            }}
            subtitle={
              meeting?.eventAt &&
              `Currently on ${formatDate(
                meeting.eventAt,
                "hh:mm a z 'on' MMMM d, yyyy",
              )}`
            }
          />
        </Box>
      </Flex>
      <div className="max-w-2/3 mx-auto">
        <div className="border border-gray-300 rounded-lg bg-white p-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner />
            </div>
          ) : (
            <TimeslotCard
              coachId={coach?.id ?? ""}
              productId={product?.id}
              requiresBookingApproval={false}
              isViewOnly={false}
              variant="side-by-side"
              selectedTimeslots={timeslots}
              showAllTimes={showAllTimes}
              setShowAllTimes={setShowAllTimes}
              start={programComponent?.targetStartDate}
              end={programComponent?.targetEndDate}
              onSelectTimeslots={(timeslots) => {
                setTimeslots(timeslots);
                setVisible(true);
              }}
              preferredTimeslots={[]}
              onPreferredChange={() => {}}
            />
          )}
        </div>
      </div>
      {error ? (
        <ErrorModal
          error={error}
          visible={visible}
          onCancel={() => {
            setError(null);
            setVisible(false);
          }}
          onConfirm={() => {
            setError(null);
            setVisible(false);
          }}
          meeting={meeting}
        />
      ) : (
        <ConfirmRescheduleDialog
          visible={visible}
          onClose={setVisible}
          coach={coach}
          product={product}
          meeting={meeting}
          timeslot={timeslots[0]}
          isRefundException={isRefundException}
          onToggleReschedule={setIsRefundException}
          rescheduleReason={rescheduleReason}
          onReasonChange={setRescheduleReason}
          onConfirm={handleReschedule}
        />
      )}
    </>
  );
}

export function ReschedulePage() {
  return (
    <Authenticate maxWidth="100%" layout="dashboard">
      <>
        <SEOHead
          title={`Reschedule your meeting - ${SITE_NAME}`}
          description="Reschedule your upcoming meetings"
          canonicalPath="/sessions/:meetingId/reschedule"
          image="https://placement-pub.s3.us-east-2.amazonaws.com/review-meta.png"
        />
        <Nav />
        <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
          <AuthReschedule />
        </Box>
      </>
    </Authenticate>
  );
}
